/**
 * M15: Campaigns CRUD for builder and dashboard (RLS: can_manage_content).
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { Database, Json } from "@/types/database"
import { generateSlug } from "@/lib/slugUtils"
import { queuePublicCachePurge } from "@/lib/publicCache"

type Campaign = Database["public"]["Tables"]["campaigns"]["Row"]
type CampaignInsert = Database["public"]["Tables"]["campaigns"]["Insert"]
type CampaignUpdate = Database["public"]["Tables"]["campaigns"]["Update"]

export interface CreateCampaignInput {
  title: string
  slug?: string
  goal_cents: number
  start_date?: string | null
  end_date?: string | null
  cover_image_url?: string | null
  content_sections?: Json
  is_published?: boolean
}

export async function isCampaignSlugTaken(
  orgId: string,
  slug: string,
  excludeCampaignId?: string | null
): Promise<boolean> {
  let q = supabase
    .from("campaigns")
    .select("id")
    .eq("org_id", orgId)
    .eq("slug", slug)
    .maybeSingle()
  const { data, error } = await q
  if (error || !data) return false
  if (excludeCampaignId && data.id === excludeCampaignId) return false
  return true
}

export function useCampaigns(orgId: string | null) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setCampaigns([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from("campaigns")
        .select("*")
        .eq("org_id", orgId)
        .order("updated_at", { ascending: false })
        .limit(100)

      if (fetchError) {
        setError(fetchError.message)
        setCampaigns([])
        return
      }
      setCampaigns((data ?? []) as Campaign[])
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { campaigns, loading, error, refresh }
}

export function useCampaign(orgId: string | null, campaignId: string | null) {
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !campaignId) {
      setCampaign(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: fetchError } = await supabase
      .from("campaigns")
      .select("*")
      .eq("id", campaignId)
      .eq("org_id", orgId)
      .maybeSingle()

    if (fetchError) {
      setError(fetchError.message)
      setCampaign(null)
    } else {
      setCampaign(data as Campaign | null)
    }
    setLoading(false)
  }, [orgId, campaignId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { campaign, loading, error, refresh }
}

export function useCreateCampaign(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createCampaign = useCallback(
    async (input: CreateCampaignInput): Promise<Campaign | null> => {
      if (!orgId) return null
      setLoading(true)
      setError(null)

      const slug = (input.slug?.trim() || generateSlug(input.title) || "campaign").toLowerCase()

      const insert: CampaignInsert = {
        org_id: orgId,
        title: input.title.trim(),
        slug,
        goal_cents: input.goal_cents,
        start_date: input.start_date || null,
        end_date: input.end_date || null,
        cover_image_url: input.cover_image_url ?? null,
        content_sections: (input.content_sections ?? []) as Json,
        is_published: input.is_published ?? false,
      }

      const { data, error: insertError } = await supabase
        .from("campaigns")
        .insert(insert)
        .select()
        .single()

      setLoading(false)
      if (insertError) {
        setError(
          insertError.code === "23505"
            ? "A campaign with this URL slug already exists."
            : insertError.message
        )
        return null
      }
      return data as Campaign
    },
    [orgId]
  )

  return { createCampaign, loading, error }
}

export function useUpdateCampaign(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateCampaign = useCallback(
    async (id: string, updates: CampaignUpdate): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { data, error: updateError } = await supabase
        .from("campaigns")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      if (updateError) {
        setError(
          updateError.code === "23505"
            ? "A campaign with this URL slug already exists."
            : updateError.message
        )
        return false
      }
      const slug = (data as { slug: string } | null)?.slug
      if (slug) {
        queuePublicCachePurge(orgId, { type: "campaign-save", campaignSlug: slug })
      }
      return true
    },
    [orgId]
  )

  const updateContentSections = useCallback(
    async (campaignId: string, sections: Json): Promise<boolean> =>
      updateCampaign(campaignId, { content_sections: sections }),
    [updateCampaign]
  )

  return { updateCampaign, updateContentSections, loading, error }
}

